import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked, cached } from '@glimmer/tracking';
import type AccountModel from 'tio-data/models/account';
import type MatchParticipantModel from 'tio-data/models/match-participant';
import type MatchPlanModel from 'tio-data/models/match-plan';
import { MATCH_PAYMENT_QUALIFICATION_STATE } from 'tio-data/models/match-payment';
import type MatchPaymentModel from 'tio-data/models/match-payment';
import type AccountTransactionModel from 'tio-data/models/account-transaction';
import { ACCOUNT_TRANSACTION_VERIFICATION_STATE } from 'tio-data/models/account-transaction';
import type MatchPaymentService from 'tio-employee/services/match-payment';

import SyfDashboard from 'tio-employee/components/syf/dashboard';

interface SyfDashboardRouteSignature {
  Args: {
    model: {
      accounts: Array<AccountModel>;
      matchParticipant: MatchParticipantModel;
      matchPlan: MatchPlanModel;
      matchPayments: Array<MatchPaymentModel>;
      accountTransactions: Array<AccountTransactionModel>;
    };
  };
}

export default class SyfDashboardRouteComponent extends Component<SyfDashboardRouteSignature> {
  @service declare matchPayment: MatchPaymentService;

  @tracked focusedAccount: AccountModel | null = null;
  @tracked showAccountVerifyAlert: boolean = false;
  @tracked showAccountUnverifyAlert: boolean = false;
  @tracked accountBorrowerConfirmed: boolean = false;
  @tracked accountVerificationError = null; // TODO: type this

  toggleAccountBorrowerConfirmed = () => {
    this.accountBorrowerConfirmed = !this.accountBorrowerConfirmed;
  };

  toggleAccountIsVerified = (account: AccountModel) => {
    this.focusedAccount = account;
    if (!account.isVerified) {
      this.showAccountVerifyAlert = true;
    } else {
      this.showAccountUnverifyAlert = true;
    }
  };

  // this goes for either possible isVerified attr boolean val
  confirmAccountIsVerified = async (account: AccountModel) => {
    account.isVerified = !account.isVerified; // toggle the isVerified attribute
    try {
      await account.save(); // save the record
      this.closeAccountIsVerifiedAlert();
    } catch (e) {
      account.isVerified = !account.isVerified; // if this didn't work, switch it back
      this.accountVerificationError = e; // set the error to ivar
    }
  };

  certifyPayments = async (payments: Array<MatchPaymentModel>) => {
    await this.matchPayment.certifyMatchPayments(payments);
  };

  closeAccountIsVerifiedAlert = () => {
    this.showAccountVerifyAlert = false;
    this.showAccountUnverifyAlert = false;
    this.accountBorrowerConfirmed = false;
    this.accountVerificationError = null;
    this.focusedAccount = null;
  };

  // match payment getters
  get certifiedPayments() {
    return this.args.model.matchPayments.filter((payment) => payment.certified);
  }

  get qualifiedPayments() {
    return this.args.model.matchPayments
      .filter(
        (payment: MatchPaymentModel) =>
          payment.qualificationState === MATCH_PAYMENT_QUALIFICATION_STATE.QUALIFIED
      )
      .filter((payment) => !payment.certified);
  }

  get unqualifiedPayments() {
    return this.args.model.matchPayments.filter(
      (payment: MatchPaymentModel) =>
        payment.qualificationState === MATCH_PAYMENT_QUALIFICATION_STATE.UNQUALIFIED
    );
  }

  @cached
  get matchPaymentByAccountTransactionId() {
    const map = new Map<string, MatchPaymentModel>();
    for (const payment of this.args.model.matchPayments) {
      const id = payment.accountTransaction?.id;
      if (id) {
        map.set(id, payment);
      }
    }
    return map;
  }

  // account transaction getters
  get eligiblePayments() {
    return this.args.model.matchPayments
      .filter(
        (p) =>
          p.qualificationState === MATCH_PAYMENT_QUALIFICATION_STATE.QUALIFIED &&
          p.accountTransaction
      )
      .map((p) => p.accountTransaction);
  }

  get unverifiedPayments() {
    return this.args.model.accountTransactions.filter((txn) => {
      return [
        ACCOUNT_TRANSACTION_VERIFICATION_STATE.PENDING_VERIFICATION,
        ACCOUNT_TRANSACTION_VERIFICATION_STATE.REJECTED,
      ].includes(txn.verificationState);
    });
  }

  get ineligiblePayments() {
    return this.args.model.matchPayments
      .filter(
        (p) =>
          p.qualificationState === MATCH_PAYMENT_QUALIFICATION_STATE.UNQUALIFIED &&
          p.accountTransaction
      )
      .map((p) => p.accountTransaction);
  }

  getPaymentQualificationState = (accountTransaction: AccountTransactionModel): string => {
    const matchPayment = this.matchPaymentByAccountTransactionId.get(accountTransaction.id ?? '');
    switch (matchPayment?.qualificationState) {
      case MATCH_PAYMENT_QUALIFICATION_STATE.QUALIFIED:
        return 'syf.dashboard.payments.qualified.status.certified';
      default:
        return '';
    }
  };

  getPaymentCertificationState = (accountTransaction: AccountTransactionModel): string => {
    const matchPayment = this.matchPaymentByAccountTransactionId.get(accountTransaction.id ?? '');
    return matchPayment?.certified
      ? 'syf.dashboard.payments.qualified.status.certified'
      : 'syf.dashboard.payments.qualified.status.pending';
  };

  <template>
    <SyfDashboard
      {{! store data }}
      @accounts={{@model.accounts}}
      @matchParticipant={{@model.matchParticipant}}
      @matchPlan={{@model.matchPlan}}
      @qualifiedPayments={{this.qualifiedPayments}}
      @unqualifiedPayments={{this.unqualifiedPayments}}
      @certifiedPayments={{this.certifiedPayments}}
      @eligiblePayments={{this.eligiblePayments}}
      @ineligiblePayments={{this.ineligiblePayments}}
      @unverifiedPayments={{this.unverifiedPayments}}
      @getPaymentQualificationState={{this.getPaymentQualificationState}}
      @getPaymentCertificationState={{this.getPaymentCertificationState}}
      @onCertifyPayments={{this.certifyPayments}}
      {{! component state and callbacks }}
      @focusedAccount={{this.focusedAccount}}
      @showAccountVerifyAlert={{this.showAccountVerifyAlert}}
      @showAccountUnverifyAlert={{this.showAccountUnverifyAlert}}
      @onToggleAccountVerified={{this.toggleAccountIsVerified}}
      @onConfirmAccountIsVerified={{this.confirmAccountIsVerified}}
      @onCancelAccountIsVerified={{this.closeAccountIsVerifiedAlert}}
      @accountBorrowerConfirmed={{this.accountBorrowerConfirmed}}
      @onToggleAccountBorrowerConfirmed={{this.toggleAccountBorrowerConfirmed}}
    />
  </template>
}
